import { useEffect, useRef, useMemo } from 'react';
import { withStore } from '@stores';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';

// Imports => Constants
import { TITLES } from '@constants';
import {AcCard, ICONS, KEYS, THEMES, TYPES} from '@opp/ui-kit';

// Imports => Hooks
import { useUIActions } from '@hooks';

// Imports => Utilities
import { AcIsSet, AcIsArray } from '@opp/ui-kit';

// Imports => Molecules
import AcCreateTicketModal from '@molecules/ac-create-ticket-modal/ac-create-ticket-modal';

// Imports => Components
import AcTicketList from '@components/ac-ticket-list/ac-ticket-list';

// Imports => Atoms
import {
  AcAvatarCard,
  AcHeading,
  AcButton,
  AcIcon,
  AcIconButton,
  AcEmptyBlock,
  AcLoaderInline,
} from '@opp/ui-kit';
import {withTranslation} from "react-i18next";

const _CLASSES = {
  MAIN: 'ac-support-center',
  OPEN: 'ac-support-center--open',
  EMPTY: 'ac-support-center--empty',
  BUTTON: 'ac-support-center__button',
  FOOTER: 'ac-support-center__footer',
};

const AcSupportCenter = ({ store: { tickets, ui }, t }) => {
  const { current_support_center } = ui;
  const { current_tickets, is_loading } = tickets;

  const $ref = useRef(null);

  const { handleClose, addEvents, removeEvents } = useUIActions(
    ui,
    KEYS.SUPPORT_CENTER,
    $ref
  );

  useEffect(() => {
    addEvents();

    return () => removeEvents();
  }, []);

  useEffect(() => {
    if (current_support_center.visible) tickets.list(t, { perpage: 999 });
  }, [current_support_center.visible]);

  const displayCreateTicketModal = async (event) => {
    if (event && (typeof event.cancelable !== 'boolean' || event.cancelable)) {
      if (event && event.preventDefault) event.preventDefault();
      if (event && event.stopPropagation) event.stopPropagation();
    }

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t('TITLES.CREATE_TICKET', {ns: 'titles'})}`,
      name: 'create-ticket-modal',
      body: <AcCreateTicketModal submit={tickets.store} />,
      closeable: true,
      visible: true,
      callback: async () => {
        await ui.reset(KEYS.MODAL);
      },
    });
  };

  const getCreateTicketButtonClassNames = useMemo(() => {
    return clsx(_CLASSES.BUTTON);
  }, []);

  const getFooterClassNames = useMemo(() => {
    return clsx(_CLASSES.FOOTER);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN, current_support_center.visible && _CLASSES.OPEN);
  }, [current_support_center.visible]);

  const renderTickets = useMemo(() => {
    const available =
            AcIsSet(current_tickets) &&
            AcIsArray(current_tickets) &&
            current_tickets.length > 0;

    if (is_loading && !available) return <AcLoaderInline loading />;
    else if (!available) {
      return (
        <AcEmptyBlock
          icon={ICONS.CELEBRATE}
          message={t('no-active-tickets', 'You have no (active) tickets to view.', {ns: 'support'})}
          border={false}
        />
      );
    }

    return <AcTicketList items={current_tickets} />;
  }, [current_tickets, is_loading]);

  const getCreateTicketButtonOptions = useMemo(() => {
    return {
      theme: THEMES.OMEGA,
      type: TYPES.BUTTON,
      callback: displayCreateTicketModal,
      className: getCreateTicketButtonClassNames,
    };
  }, [displayCreateTicketModal, getCreateTicketButtonClassNames]);

  return (
    <div className={getMainClassNames}>
      <div className={'ac-off-canvas__section h-margin-y-0'}>
        <AcHeading
          rank={4}
          theme={THEMES.DARK}
          className={'h-margin-top-0 h-margin-bottom-0'}
        >
          <AcIconButton
            icon={ICONS.CLOSE}
            theme={THEMES.EGGWHITE}
            className={'h-margin-right-20'}
            callback={handleClose}
            flat
            title={`${t('close', 'Close', {ns: 'common'})}`}
          />
          {t('TITLES.SUPPORT_AND_TICKETS', {ns: 'titles'})}
        </AcHeading>
      </div>

      <div
        className={
          'ac-off-canvas__section ac-off-canvas__section--flex h-padding-top-0 h-margin-top-0 h-margin-bottom-auto'
        }
      >
        {renderTickets}
      </div>

      <div className={getFooterClassNames}>
        <AcButton {...getCreateTicketButtonOptions}>
          <span>{t('create-new-ticket', {ns: 'support'})}</span>
          <AcIcon icon={ICONS.PLUS} title={t('create-new-ticket', {ns: 'support'})} />
        </AcButton>
      </div>
    </div>
  );
};

export default withTranslation(['support', 'titles', 'store'])(withStore(observer(AcSupportCenter)));
